import { ArrowLeftOutlined } from '@ant-design/icons';
import { useLazyQuery, useMutation } from '@apollo/client';
import { Alert, Button, Card, Form, Spin, message } from 'antd';
import dayjs from 'dayjs';
import React, { useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { MODULES, ROUTES } from '../../../../../common/constants';
import GoBackButton from '../../../../../components/GoBackButton';
import Portal from '../../../../../components/Portal';
import UserPayoutsLinksForm from '../components/UserPayoutsLinksForm';
import { UPDATE_PAYMENT_LINK } from '../graphql/Mutations';
import { GET_PAYMENT_LINK } from '../graphql/Queries';

const EditUserPayoutLink = () => {
  const { id } = useParams();
  const history = useHistory();
  const [form] = Form.useForm();

  const [getPaymentLink, { data, loading, error }] = useLazyQuery(
    GET_PAYMENT_LINK,
    {
      fetchPolicy: 'network-only',
      onCompleted: (response) => {
        if (response?.personalizedOffer?.data) {
          form.setFieldsValue({
            ...response?.personalizedOffer?.data,
            plan: response?.personalizedOffer?.data?.plan?.id,
            expiryDate: dayjs(response?.personalizedOffer?.data?.expiryDate),
          });
        }
      },
      onError: (e) => {},
    },
  );

  useEffect(() => {
    getPaymentLink({ variables: { where: { id } } });
  }, [getPaymentLink, id]);

  const [updatePaymentLink, { loading: updateLoading }] = useMutation(
    UPDATE_PAYMENT_LINK,

    {
      onCompleted: () => {
        history.push(`${ROUTES?.PAYMENTS}${ROUTES?.USER_PAYOUT_LINKS}`);
      },
      onError: () => {
        message.error('Failed to update payment link');
      },
    },
  );

  useEffect(() => {
    if (data?.personalizedOffer?.data) {
      form.setFieldsValue({
        ...data?.personalizedOffer?.data,
        plan: data?.personalizedOffer?.data?.plan?.id,
        expiryDate: dayjs(data?.personalizedOffer?.data?.expiryDate),
      });
    }
  }, [data, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      await updatePaymentLink({
        variables: {
          where: {
            id,
          },
          data: {
            ...values,
            planId: values.plan,
            plan: undefined,
            expiryDate: values.expiryDate.toISOString(),
          },
        },
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Error updating payment link:', e);
    }
  };

  if (loading) {
    return (
      <div className="spinner-container">
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <Alert
          message="Error"
          description="There was an error fetching the payment link details. Please try again later."
          type="error"
          showIcon
        />
      </div>
    );
  }

  return (
    <>
      <Portal portalId="header-title-content">
        <GoBackButton
          customLink={`${ROUTES?.PAYMENTS}${ROUTES?.USER_PAYOUT_LINKS}/view/${id}`}
        />
        <div className="portal-header">{MODULES.USER_PAYOUT_LINKS}</div>
      </Portal>
      <Portal portalId="header-right-content">
        <Button
          className="common-button"
          icon={<ArrowLeftOutlined />}
          onClick={() =>
            history.push(`${ROUTES?.PAYMENTS}${ROUTES?.USER_PAYOUT_LINKS}`)
          }
        >
          Cancel
        </Button>
        <Button
          type="primary"
          className="common-button ml-16"
          onClick={handleSubmit}
          loading={updateLoading}
        >
          Update
        </Button>
      </Portal>
      <Portal portalId="header-right-content-phones">
        <Button
          className="common-button"
          icon={<ArrowLeftOutlined />}
          onClick={() =>
            history.push(`${ROUTES?.PAYMENTS}${ROUTES?.USER_PAYOUT_LINKS}`)
          }
        >
          Cancel
        </Button>
        <Button
          type="primary"
          className="common-button ml-16"
          onClick={handleSubmit}
          loading={updateLoading}
        >
          Update
        </Button>
      </Portal>
      <Card className="full-height-without-nav">
        <UserPayoutsLinksForm form={form} isEdit />
      </Card>
    </>
  );
};

export default EditUserPayoutLink;
